import { Injectable } from "@angular/core";
import {
  Http,
  RequestOptions,
  Response,
  ResponseContentType
} from "@angular/http";
import { Observable } from "rxjs";
import { saveAs } from "file-saver";
import BaseModel from "@builtbybros/al-model/models/BaseModel";

import { QueryParam } from "./query-param";
import { AuthService } from "../auth/auth.service";
import { environment } from "../../environments/environment";

@Injectable()
export class HttpService {
  constructor(private http: Http, private authService: AuthService) {}

  get<T, U extends BaseModel>(
    url: string,
    id: number,
    result: U
  ): Observable<U> {
    const options = this.authService.addAuthToken();

    return this.http
      .get(this.getEndpointUrl(url) + id, options)
      .map(res => {
        const json = res.json();
        return result.parse(json);
      })
      .catch((error: any) => {
        this.handleError(error);
        return Observable.throw(error.json().message || "Server error");
      });
  }

  getWithParams<U extends BaseModel>(
    url: string,
    queryParams: QueryParam[],
    type: { new (): U }
  ): Observable<U> {
    const options = this.authService.addAuthToken();
    const fullUrl = this.getEndpointUrl(url);
    const urlWithParams = this.addParamsToUrl(fullUrl, queryParams);
    const result = new type();

    return this.http
      .get(urlWithParams, options)
      .map(res => {
        const json = res.json();
        return result.parse(json);
      })
      .catch((error: any) => {
        return this.handleError(error);
      });
  }

  getRaw(url: string, queryParams: QueryParam[]): Observable<JSON> {
    const options = this.authService.addAuthToken();
    const fullUrl = this.getEndpointUrl(url);
    const urlWithParams = this.addParamsToUrl(fullUrl, queryParams);
    return this.http
      .get(urlWithParams, options)
      .map(res => {
        return res.json();
      })
      .catch((error: any) => {
        return this.handleError(error);
      });
  }

  postRaw(url: string, queryParams: QueryParam[], body: any): Observable<JSON> {
    const options = this.authService.addAuthToken();
    const fullUrl = this.getEndpointUrl(url);
    const urlWithParams = this.addParamsToUrl(fullUrl, queryParams);
    return this.http
      .post(urlWithParams, body, options)
      .map(res => {
        return res.json();
      })
      .catch((error: any) => {
        return this.handleError(error);
      });
  }

  getObjects<T, U extends BaseModel>(
    url: string,
    id: number,
    type: { new (): U }
  ): Observable<U[]> {
    const options = this.authService.addAuthToken();

    return this.http
      .get(this.getEndpointUrl(url) + id, options)
      .map(res => {
        let json = res.json();
        json = !json ? [] : json;
        let results = [];
        for (let item of json) {
          const result = new type();
          results.push(result.parse(item));
        }
        return results;
      })
      .catch((error: any) => {
        this.handleError(error);
        return Observable.throw(error.json().message || "Server error");
      });
  }

  post<T, U extends BaseModel>(
    url: string,
    objects: T,
    result: U
  ): Observable<U> {
    const options = this.authService.addAuthToken();
    return this.http
      .post(this.getEndpointUrl(url), objects, options)
      .map(res => {
        const json = res.json();
        return result.parse(json);
      })
      .catch((error: any) => {
        this.handleError(error);
        return Observable.throw(error.json().message || "Server error");
      });
  }

  patch<T, U extends BaseModel>(
    id: number,
    url: string,
    objects: T,
    result?: U
  ): Observable<U> {
    const options = this.authService.addAuthToken();
    return this.http
      .patch(this.getEndpointUrl(url) + id, objects, options)
      .map(res => {
        const json = res.json();
        return result ? result.parse(json) : json;
      })
      .catch((error: any) => {
        this.handleError(error);
        return Observable.throw(error.json().message || "Server error");
      });
  }

  updateRaw(url: string, body) {
    const options = this.authService.addAuthToken();
    return this.http
      .patch(this.getEndpointUrl(url), body, options)
      .map(res => {
        const json = res.json();
        return json;
      })
      .catch(error => {
        this.handleError(error);
        return Observable.throw(error.json().message || "Server error");
      });
  }

  postFile(url: string, file: File, filename?: string): Observable<boolean> {
    const options = this.authService.addAuthToken();
    const formData = new FormData();

    //Todo: send filename
    formData.append("image", file);
    return this.http
      .post(this.getEndpointUrl(url) + `/${filename}`, formData, options)
      .map(res => {
        return true;
      })
      .catch((error: any) => {
        this.handleError(error);
        return Observable.throw(error.json().message || "Server error");
      });
  }

  getCoverUrl(url: string): string {
    return environment.coversUrl + url;
  }

  downloadImage(url: string): Observable<File> {
    const options = new RequestOptions({
      responseType: ResponseContentType.Blob
    });
    return this.http
      .get(environment.coversUrl + url, options)
      .map(res => {
        return new Blob([res["_body"]], {
          type: "image/*"
        });
      })
      .catch((error: any) => {
        this.handleError(error);
        return Observable.throw(error.json().message || "Server error");
      });
  }

  /**
   * Función generica para forzar descarga de archivos.
   *
   * @todo Obtener el nombre del archivo desde la cabecera HTTP de respuesta.
   */
  downloadFile(
    url: string,
    filename: string,
    query?: string,
    actives?: boolean
  ): Observable<void> {
    const options = this.authService.addAuthToken();
    options.responseType = ResponseContentType.Blob;
    // options.observe = 'response';
    const queryParam = query ? `query=${query}` : ``;
    const activesParam = actives !== null ? `&actives=${actives}` : ``;
    const finalUrl = this.getEndpointUrl(url) + "?" + queryParam + activesParam;
    return this.http.get(finalUrl, options).map(res => {
      saveAs(res["_body"], filename);
    });
  }

  delete(url: string, id?: number): Observable<number> {
    const options = this.authService.addAuthToken();
    const finalUrl = id
      ? this.getEndpointUrl(url) + id
      : this.getEndpointUrl(url);

    return this.http
      .delete(finalUrl, options)
      .map(res => {
        return res;
      })
      .catch((error: any) => {
        this.handleError(error);
        return Observable.throw(error.json().message || "Server error");
      });
  }

  private getEndpointUrl(url: string): string {
    return environment.alApiUrl + url;
  }

  private addParamsToUrl(fullUrl: string, params: QueryParam[]): string {
    let query = "";
    for (const param of params) {
      query += `&${param.name}=${param.value}`;
    }
    query = query.substr(1, query.length - 1);
    return `${fullUrl}?${query}`;
  }

  private handleError(error: Response | any) {
    // Todo: log error and activity using a remote infrastructure
    let errMsg: string;
    if (error instanceof Response) {
      const body = error.json() || "";
      const err = body.error || JSON.stringify(body);
      errMsg = `${error.status} - ${error.statusText || ""} ${err}`;
    } else {
      errMsg = error.message ? error.message : error.toString();
    }
    console.error(errMsg);
    return Observable.throw(errMsg);
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/http.service.ts